import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from '../utils/i18n';
import { getContent } from '../utils/contentStore';
const paintDrops = [{
  color: '#DC2626',
  left: '8%',
  width: 60,
  height: 180,
  delay: '0s',
  duration: '4s'
}, {
  color: '#e5e7eb',
  left: '18%',
  width: 40,
  height: 120,
  delay: '1.2s',
  duration: '5s'
}, {
  color: '#d1d5db',
  left: '30%',
  width: 80,
  height: 220,
  delay: '0.4s',
  duration: '6s'
}, {
  color: '#e2e8f0',
  left: '44%',
  width: 50,
  height: 160,
  delay: '2s',
  duration: '4.5s'
}, {
  color: '#f3f4f6',
  left: '57%',
  width: 70,
  height: 200,
  delay: '0.8s',
  duration: '5.5s'
}, {
  color: '#d1d5db',
  left: '68%',
  width: 45,
  height: 140,
  delay: '1.6s',
  duration: '3.8s'
}, {
  color: '#cbd5e1',
  left: '79%',
  width: 90,
  height: 250,
  delay: '0.2s',
  duration: '6.5s'
}, {
  color: '#e2e8f0',
  left: '90%',
  width: 35,
  height: 110,
  delay: '2.4s',
  duration: '4.2s'
}];
const swatchColors = ['#DC2626', '#B91C1C', '#ef4444', '#1e293b', '#334155', '#475569', '#f8fafc', '#e2e8f0', '#cbd5e1', '#DC2626', '#7f1d1d', '#fca5a5', '#111827', '#374151', '#6b7280'];
export function Hero() {
  const [paintColor, setPaintColor] = useState<string | null>(null);
  const [fillActive, setFillActive] = useState(false);
  const [fillKey, setFillKey] = useState(0);
  const {
    t,
    loc
  } = useLanguage();
  const content = getContent().hero;
  const handleSwatchEnter = useCallback((color: string) => {
    setPaintColor(color);
    setFillActive(true);
    setFillKey(k => k + 1);
  }, []);
  const handleSwatchLeave = useCallback(() => {
    setFillActive(false);
  }, []);
  return <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-20" style={{
    background: '#ffffff'
  }} data-id="element-240">
      
      {/* Paint drip blobs from top */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" data-id="element-241">
        {paintDrops.map((drop, i) => <div key={i} className="absolute top-0 paint-drip" style={{
        left: drop.left,
        width: drop.width,
        animationDelay: drop.delay,
        animationDuration: drop.duration
      }} data-id="element-242">
          
            <div style={{
          width: '100%',
          height: drop.height,
          background: drop.color,
          opacity: 0.5,
          borderRadius: '0 0 50% 50%'
        }} data-id="element-243" />
          
            <div style={{
          width: drop.width * 0.55,
          height: drop.width * 0.75,
          background: drop.color,
          opacity: 0.5,
          borderRadius: '50%',
          margin: '-2px auto 0'
        }} data-id="element-244" />
          
          </div>)}
        {paintDrops.map((drop, i) => <div key={`splash-${i}`} className="absolute paint-splash" style={{
        left: `calc(${drop.left} - ${drop.width * 0.5}px)`,
        top: drop.height + drop.width * 0.75 + 2,
        width: drop.width * 2.5,
        height: drop.width * 0.5,
        background: drop.color,
        opacity: 0.2,
        borderRadius: '50%',
        animationDelay: drop.delay,
        animationDuration: drop.duration,
        filter: 'blur(4px)'
      }} data-id="element-245" />)}
      </div>

      {/* Light overlay for depth */}
      <div className="absolute inset-0 z-10 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.7) 70%)'
    }} data-id="element-246" />
      

      {/* Main Content */}
      <div className="container mx-auto px-4 relative z-20 text-center flex-1 flex items-center justify-center" data-id="element-247">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        ease: 'easeOut'
      }} className="max-w-5xl mx-auto" data-id="element-248">
          
          <motion.div initial={{
          opacity: 0,
          scaleX: 0
        }} animate={{
          opacity: 1,
          scaleX: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="flex items-center justify-center gap-3 mb-8" data-id="element-249">
            
            <span className="h-px w-12 bg-gray-400 block" data-id="element-250" />
            <span className="text-gray-500 text-xs font-bold tracking-[0.3em] uppercase" data-id="element-251">
              {loc(content.subtitle)}
            </span>
            <span className="h-px w-12 bg-gray-400 block" data-id="element-252" />
          </motion.div>

          {/* YAVUZ BURAK heading with paint-fill effect */}
          <h1 className="font-heading text-6xl md:text-8xl lg:text-[10rem] font-bold uppercase leading-none mb-6 tracking-tight select-none" data-id="element-253">
            {/* YAVUZ — interactive paint fill */}
            <span className="relative inline-block mr-4 md:mr-6 lg:mr-8" data-id="element-254">
              {/* Base layer */}
              <span className="relative z-10 text-gray-900" data-id="element-255">YAVUZ</span>

              {/* Paint fill layer — clips from bottom to top */}
              <span key={fillKey} aria-hidden className="absolute inset-0 z-20 overflow-hidden pointer-events-none" style={{
              clipPath: fillActive ? 'polygon(0% 100%, 3% 95%, 7% 100%, 12% 93%, 18% 100%, 25% 94%, 32% 100%, 40% 95%, 48% 100%, 55% 93%, 63% 100%, 70% 95%, 78% 100%, 85% 94%, 92% 100%, 100% 95%, 100% 0%, 0% 0%)' : 'polygon(0% 100%, 3% 100%, 7% 100%, 12% 100%, 18% 100%, 25% 100%, 32% 100%, 40% 100%, 48% 100%, 55% 100%, 63% 100%, 70% 100%, 78% 100%, 85% 100%, 92% 100%, 100% 100%, 100% 100%, 0% 100%)',
              transition: fillActive ? 'clip-path 0.65s cubic-bezier(0.22, 1, 0.36, 1)' : 'clip-path 0.4s ease-in',
              color: paintColor || '#DC2626',
              WebkitTextStroke: '0px'
            }} data-id="element-256">
                
                YAVUZ
              </span>
            </span>

            {/* BURAK — outline red */}
            <span className="text-transparent" style={{
            WebkitTextStroke: '2px #DC2626'
          }} data-id="element-257">
              
              BURAK
            </span>
          </h1>

          {/* Paint hint tooltip */}
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: paintColor ? 0 : 1
        }} transition={{
          duration: 0.3,
          delay: 1.5
        }} className="text-gray-400 text-xs mb-2 -mt-4 tracking-widest uppercase" data-id="element-258">
            
            {loc(content.hint)}
          </motion.p>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.4
        }} className="font-heading text-lg md:text-2xl font-semibold tracking-widest mb-8 uppercase text-gray-700" data-id="element-259">
            
            {loc(content.title2)}
          </motion.h2>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.7,
          delay: 0.6
        }} className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto mb-12 leading-relaxed font-light" data-id="element-260">
            
            {loc(content.description)}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.8
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-261">
            
            <Button size="lg" className="w-full sm:w-auto min-w-[220px] group" onClick={() => document.getElementById('catalog')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-262">
              
              {loc(content.btnCatalog)}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-263" />
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto min-w-[220px]" onClick={() => document.getElementById('contact')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-264">
              
              {loc(content.btnContact)}
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Color Swatch Strip — interactive */}
      <div className="relative z-20 w-full mt-auto" data-id="element-265">
        <div className="overflow-hidden h-12 flex" data-id="element-266">
          <div className="swatch-scroll flex h-full" data-id="element-267">
            {[...swatchColors, ...swatchColors].map((color, i) => <div key={i} className="flex-shrink-0 h-full cursor-crosshair relative group" style={{
            width: `${100 / swatchColors.length}vw`,
            background: color
          }} onMouseEnter={() => handleSwatchEnter(color)} onMouseLeave={handleSwatchLeave} data-id="element-268">
              
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{
              background: 'rgba(0,0,0,0.15)'
            }} data-id="element-269" />
              
                <div className="absolute inset-x-0 bottom-0 h-0 group-hover:h-3 transition-all duration-200" style={{
              background: 'rgba(0,0,0,0.2)'
            }} data-id="element-270" />
              
              </div>)}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 text-gray-400" animate={{
      y: [0, 10, 0]
    }} transition={{
      repeat: Infinity,
      duration: 2
    }} data-id="element-271">
        
        <ChevronDown className="h-7 w-7" data-id="element-272" />
      </motion.div>
    </section>;
}